import React, { useState, useEffect, useRef, useCallback } from 'react';
import { string, oneOf } from 'prop-types';
import './endcap.scss';
import {
  useDataModel,
  string as stringType,
  arrayOf,
  shape as shapeType,
  number,
  bool as boolType,
  extend,
  params,
  customType,
} from '@thd-nucleus/data-sources';
import { Col, Row } from '@thd-olt-component-react/grid';
import { useStore } from '@thd-nucleus/experience-context';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { TopBar } from './TopBar';
import { HeaderSection } from './HeaderSection';
import { CategoryTabs } from './CategoryTabs';
import { CategoryCarousel } from './CategoryCarousel';
import { EndcapPlaceholder } from './EndcapPlaceholder';

const Endcap = ({
  customerType,
  pageUrl,
  channel,
  storeId,
  personalizedDealsUserid,
  categoryGuid,
  entryId
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('endcap.ready');
  }, []);

  const { storeZip: zipCode } = useStore();
  const [navParam, setNavParam] = useState(null);
  const [newCategoryGuid, setNewCategoryGuid] = useState(categoryGuid);
  const [recsAlgorithm, setRecsAlgorithm] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const eventsMetadata = useRef({});
  const endCapMetadata = useRef({});
  const localStoreId = useRef(storeId);
  const options = {
    variables: {
      customerType,
      pageUrl,
      channel,
      storeId,
      personalizedDealsUserid,
      categoryGuid: newCategoryGuid,
      maxProducts: 15,
      shouldSkipMetadata: !!navParam,
      skipInstallServices: false,
      navParam,
      recsAlgorithm,
      skipSpecificationGroup: false,
      zipCode,
    },
  };

  const { data, loading, error, variables } = useDataModel('endCap', options);

  if (data && !loading) {
    localStoreId.current = variables.storeId;
  }

  if (data?.endCap?.eventsMetadata) {
    eventsMetadata.current = {
      ...data?.endCap?.eventsMetadata,
      categories: data?.endCap.personalizedDealsEndCap
      && data?.endCap?.eventsMetadata?.categories?.length > 1
        ? [
          {
            category: 'All',
            nValue: null,
            imageUrl: null,
            url: null,
            shopAllLink: null,
            exclude: null,
            displayImageOnMobileWeb: false,
          },
          ...data?.endCap?.eventsMetadata?.categories,
        ]
        : data?.endCap?.eventsMetadata?.categories,
    };
  }
  if (data?.endCap?.topDealsEndCap) {
    endCapMetadata.current = data?.endCap?.topDealsEndCap;
  } else if (data?.endCap?.personalizedDealsEndCap?.products) {
    endCapMetadata.current = data?.endCap?.personalizedDealsEndCap;
  } else {
    endCapMetadata.current = {};
  }

  const handleTabClick = useCallback(
    (event, index, nValue, nameOfEventAttribute, changeActiveIndex) => {
      event.preventDefault();
      let eventAttributeName = nameOfEventAttribute !== 'Model2' ? nameOfEventAttribute : 'Model2';
      changeActiveIndex(index);
      setActiveIndex(index);
      setNavParam(nValue);
      setNewCategoryGuid(nValue);
      setRecsAlgorithm(eventAttributeName);

      if (typeof window !== 'undefined') {
        const swiperElement = document.querySelector(
          '.endcap .swiper-container'
        );
        if (swiperElement) swiperElement.swiper.slideTo(0);
      }
    },
    []
  );

  if (!data && !error && loading) {
    return (
      <EndcapPlaceholder />
    );
  }

  if (handleTabClick && loading) {
    return (
      <Row className="endcap">
        <Col xs="12" sm="12" md="12" lg="12" nopadding>
          <TopBar gradientColors={eventsMetadata.current?.topGradientValues} />
        </Col>
        <Col xs="12" sm="12" md="12" lg="12" nopadding>
          <HeaderSection
            header={eventsMetadata.current?.header}
            start={eventsMetadata.current?.start}
            end={eventsMetadata.current?.end}
            bannerImage={eventsMetadata.current?.bannerImage}
            mobileAppBannerImg={eventsMetadata.current?.mobileAppBannerImg}
            headerLinkText={eventsMetadata.current?.headerLinkText}
            headerLinkUrl={eventsMetadata.current?.headerLinkUrl}
            timerDuration={eventsMetadata.current?.timerDuration}
            badgeUrl={eventsMetadata.current?.badgeUrl}
            channel={channel}
            timer={eventsMetadata.current?.timer}
            subHeader={eventsMetadata.current?.subHeader}
          />
        </Col>
        {eventsMetadata.current?.categories?.length > 1 && (
          <Col xs="12" sm="12" md="12" lg="12" nopadding>
            <CategoryTabs
              categories={eventsMetadata.current?.categories}
              endCapData={endCapMetadata.current}
              nameOfEventAttribute={
                  eventsMetadata.current?.nameOfEventAttribute || 'Model2'
              }
              channel={channel}
              title={eventsMetadata.current?.header}
              handleTabClick={handleTabClick}
            />
            <EndcapPlaceholder tabClick />
          </Col>
        )}
        {eventsMetadata.current?.categories?.length === 1 && (
          <Col xs="12" sm="12" md="12" lg="12" nopadding>
            <EndcapPlaceholder tabClick />
          </Col>
        )}
      </Row>
    );
  }

  if ((!data && !loading) || (error && !data)) {
    return null;
  }
  if (!eventsMetadata.current) return null;

  return (
    <Row className="endcap">
      <Col xs="12" sm="12" md="12" lg="12" nopadding>
        <TopBar gradientColors={eventsMetadata.current?.topGradientValues} />
      </Col>
      <Col xs="12" sm="12" md="12" lg="12" nopadding>
        <HeaderSection
          header={eventsMetadata.current?.header}
          start={eventsMetadata.current?.start}
          end={eventsMetadata.current?.end}
          bannerImage={eventsMetadata.current?.bannerImage}
          mobileAppBannerImg={eventsMetadata.current?.mobileAppBannerImg}
          headerLinkText={eventsMetadata.current?.headerLinkText}
          headerLinkUrl={eventsMetadata.current?.headerLinkUrl}
          timerDuration={eventsMetadata.current?.timerDuration}
          badgeUrl={eventsMetadata.current?.badgeUrl}
          channel={channel}
          timer={eventsMetadata.current?.timer}
          subHeader={eventsMetadata.current?.subHeader}
        />
      </Col>
      {eventsMetadata.current?.categories?.length > 1 && (
        <Col xs="12" sm="12" md="12" lg="12" nopadding>
          <CategoryTabs
            categories={eventsMetadata.current?.categories}
            endCapData={endCapMetadata.current}
            nameOfEventAttribute={
              eventsMetadata.current?.nameOfEventAttribute || 'Model2'
            }
            channel={channel}
            title={eventsMetadata.current?.header}
            handleTabClick={handleTabClick}
          />
          <CategoryCarousel
            endCapData={endCapMetadata.current}
            exclude={eventsMetadata.current?.categories[activeIndex].exclude}
            categoryName={
              eventsMetadata.current?.categories[activeIndex].category
            } // eslint-disable-line
            categoryImageURL={
              eventsMetadata.current?.categories[activeIndex].imageUrl
            } // eslint-disable-line
            categoryURL={eventsMetadata.current?.categories[activeIndex].url}
            categoryShopAllLink={eventsMetadata.current?.categories[activeIndex].shopAllLink}
            channel={channel}
            displayImageOnMobileWeb={
              eventsMetadata.current?.categories[activeIndex]
                .displayImageOnMobileWeb
            } // eslint-disable-line
            title={eventsMetadata.current?.header}
            componentName={eventsMetadata.current?.apiType}
            entryId={entryId}
          />
        </Col>
      )}
      {eventsMetadata.current?.categories?.length === 1 && (
        <Col xs="12" sm="12" md="12" lg="12" nopadding>
          <CategoryCarousel
            endCapData={endCapMetadata.current}
            exclude={eventsMetadata.current?.categories[0].exclude}
            categoryName={eventsMetadata.current?.categories[0].category}
            categoryImageURL={eventsMetadata.current?.categories[0].imageUrl}
            categoryURL={eventsMetadata.current?.categories[0].url}
            channel={channel}
            displayImageOnMobileWeb={
              eventsMetadata.current?.categories[0].displayImageOnMobileWeb
            }
            title={eventsMetadata.current?.header}
            componentName={eventsMetadata.current?.apiType}
            entryId={entryId}
          />
        </Col>
      )}
    </Row>
  );
};

Endcap.displayName = 'Endcap';

const productModel = extend(
  {},
  {
    itemId: stringType(),
    dataSource: stringType(),
  },
  ProductPod.dataModel.product
);

const wrappedProduct = shapeType({
  itemId: stringType(),
  product: shapeType(productModel),
});

const customerTypes = {
  B2C: Symbol('B2C'),
  B2B: Symbol('B2B'),
  B2X: Symbol('B2X'),
};

Endcap.dataModel = extend({
  endCap: params({
    pageUrl: stringType().isRequired(),
    channel: customType('Channel').enum(['DESKTOP', 'MOBILE'], 'DESKTOP'),
    storeId: stringType().isRequired(),
    personalizedDealsUserid: stringType(),
    customerType: customType('CustomerType').enum(['B2C', 'B2B', 'B2X'], 'B2C'),
    categoryGuid: stringType(),
  }).shape({
    // TODO removing this id for now which prevents the products from updating when clicking a previously-clicked tab
    // id: stringType(),
    eventsMetadata: shapeType({
      apiType: stringType(),
      title: stringType(),
      start: stringType(),
      end: stringType(),
      bannerImage: stringType(),
      mobileAppBannerImg: stringType(),
      headerLinkText: stringType(),
      headerLinkUrl: stringType(),
      timerDuration: number({ long: true }),
      topGradientValues: arrayOf(stringType()),
      badgeUrl: stringType(),
      categories: arrayOf(
        shapeType({
          category: stringType(),
          nValue: stringType(),
          imageUrl: stringType(),
          url: stringType(),
          shopAllLink: stringType(),
          exclude: arrayOf(stringType()),
          displayImageOnMobileWeb: boolType(),
        })
      ),
      nameOfEventAttribute: stringType(),
      header: stringType(),
      iconImage: stringType(),
      subHeader: stringType(),
      timer: shapeType({
        timeBombThreshold: stringType(),
        daysLeftThreshold: stringType(),
        dateDisplayThreshold: stringType(),
        message: stringType(),
      }),
    }).skip('shouldSkipMetadata', false),
    topDealsEndCap: params({
      navParam: stringType(),
      recsAlgorithm: stringType(),
      maxProducts: number({ int: true }).isRequired(),
    }).shape({
      nvalue: stringType(),
      catName: stringType(),
      catImage: stringType(),
      catURL: stringType(),
      products: params().shape(productModel),
    }),
    personalizedDealsEndCap: shapeType({
      catName: stringType(),
      catImage: stringType(),
      catURL: stringType(),
      nvalue: stringType(),
      products: params().shape(productModel),
    }),
  }),
});

Endcap.propTypes = {
  pageUrl: string.isRequired,
  channel: oneOf(['DESKTOP', 'MOBILE']).isRequired,
  storeId: string.isRequired,
  personalizedDealsUserid: string,
  customerType: oneOf(['B2C', 'B2B', 'B2X']).isRequired,
  categoryGuid: string,
  entryId: string,
};

Endcap.defaultProps = {
  personalizedDealsUserid: '',
  categoryGuid: null,
  entryId: ''
};

Endcap.defaultProps = {
  personalizedDealsUserid: '',
};

export { Endcap };
